import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import styles from './LoginPage.module.css'

export function LoginPage() {
  const { isAuthenticated, requestOtp, verifyOtp } = useAuth()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [otp, setOtp] = useState('')
  const [step, setStep] = useState<'enter' | 'verify'>('enter')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)

    try {
      await requestOtp({ email, name, mobile })
      setOtp('')
      setInfo(`A 6-digit verification code has been sent to ${email}`)

      setStep('verify')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await verifyOtp({ email, otp })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.logo} aria-hidden="true">📊</span>
          <h1 className={styles.title}>Facebook CampaignOS</h1>
          <p className={styles.subtitle}>
            AI-powered Facebook campaign management platform
          </p>
        </div>

        {step === 'enter' && (
          <div className={styles.featureList}>
            <span className={styles.featureItem}>✦ AI Campaign Generation</span>
            <span className={styles.featureItem}>✦ Meta Ads Publishing</span>
            <span className={styles.featureItem}>✦ Real-time Analytics</span>
          </div>
        )}

        <form
          className={styles.form}
          onSubmit={step === 'enter' ? handleRequestOtp : handleVerifyOtp}
        >
          {step === 'enter' && (
            <>
              <div className={styles.field}>
                <label htmlFor="name" className={styles.label}>Full name</label>
                <input
                  id="name"
                  type="text"
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  required
                  disabled={loading}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>Work email</label>
                <input
                  id="email"
                  type="email"
                  className={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  disabled={loading}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="mobile" className={styles.label}>Phone number</label>
                <input
                  id="mobile"
                  type="tel"
                  className={styles.input}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+91 98765 43210"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}

          {step === 'verify' && (
            <div className={styles.field}>
              <label htmlFor="otp" className={styles.label}>Verification code</label>
              <input
                id="otp"
                type="text"
                className={styles.input}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                required
                disabled={loading}
                autoComplete="one-time-code"
              />
            </div>
          )}

          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          {/* ✅ FIX: info message now shows a helpful hint, never the raw OTP */}
          {info && (
            <div
              className={styles.error}
              role="status"
              style={{ color: '#16a34a', background: '#f0fdf4', borderColor: '#bbf7d0' }}
            >
              {info}
            </div>
          )}

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={
              loading ||
              (step === 'enter' && (!email || !name || !mobile)) ||
              (step === 'verify' && !otp)
            }
          >
            {loading
              ? 'Working...'
              : step === 'enter'
              ? 'Send verification code'
              : 'Verify and sign in'}
          </button>

          {step === 'verify' && (
            <button
              type="button"
              className={styles.submitBtn}
              style={{
                background: 'transparent',
                color: '#4f46e5',
                border: '1px solid #4f46e5',
                marginTop: '0.5rem',
              }}
              onClick={() => {
                setStep('enter')
                setOtp('')
                setError(null)
                setInfo(null)
              }}
              disabled={loading}
            >
              Back
            </button>
          )}
        </form>

        {step === 'verify' && (
          <p className={styles.verifyHint}>
            Enter the 6-digit code sent to <strong>{email}</strong>
          </p>
        )}
      </div>
    </div>
  )
}
